/**
 * Canvas access token storage.
 *
 * The token is pasted by the user at runtime and lives in IndexedDB on the
 * device. It is never a build-time environment variable, because Vite inlines
 * every `VITE_`-prefixed variable into the compiled bundle, which would put the
 * token in the deployed static files where anyone loading the site could read
 * it — strictly worse than IndexedDB (brainstorm.md 1.15).
 *
 * It is also never sent to the worker as a stored secret. The worker is a dumb
 * relay with no standing authority of its own; the token travels per-request in
 * the `Authorization` header and nowhere else (brainstorm.md 1.15a).
 *
 * Accepted residual risk, from brainstorm.md 1.3: a managed Chromebook's admin
 * policies and any force-installed DLP extension can read page contents,
 * including this. Known and accepted for personal use. IndexedDB is not a
 * secure enclave and this code does not pretend otherwise — what it buys is
 * that the token is not in the build output, not in git, and not on a server.
 */
import { openDB, type IDBPDatabase } from 'idb';

const DB_NAME = 'cgc';
const DB_VERSION = 1;
const STORE = 'auth';
const TOKEN_KEY = 'canvas-token';
const META_KEY = 'canvas-token-meta';

export interface TokenMeta {
  /** When the token was saved, epoch ms. */
  savedAt: number;
  /** Result of the last "test connection", for the Settings screen. */
  lastVerifiedAt?: number;
  /** Display name returned by `/users/self`, so Settings can show who it is. */
  userName?: string;
}

let dbPromise: Promise<IDBPDatabase> | null = null;

function db(): Promise<IDBPDatabase> {
  dbPromise ??= openDB(DB_NAME, DB_VERSION, {
    upgrade(database) {
      if (!database.objectStoreNames.contains(STORE)) {
        database.createObjectStore(STORE);
      }
    },
  });
  return dbPromise;
}

/**
 * In-memory mirror so the synchronous request path does not have to await
 * IndexedDB on every call. Populated by `loadToken()` at startup and kept in
 * step by `saveToken` / `clearToken`.
 */
let cachedToken: string | null = null;
let cacheLoaded = false;

export async function loadToken(): Promise<string | null> {
  try {
    const value = await (await db()).get(STORE, TOKEN_KEY);
    cachedToken = typeof value === 'string' && value.length > 0 ? value : null;
  } catch {
    // IndexedDB can be unavailable (private browsing, device policy). The app
    // still works; the user is asked for a token again.
    cachedToken = null;
  }
  cacheLoaded = true;
  return cachedToken;
}

/** The token, if `loadToken()` has run. Used by the synchronous request path. */
export function getCachedToken(): string | null {
  return cachedToken;
}

export function isTokenLoaded(): boolean {
  return cacheLoaded;
}

export async function saveToken(token: string, meta?: Partial<TokenMeta>): Promise<void> {
  const trimmed = token.trim();
  cachedToken = trimmed.length > 0 ? trimmed : null;
  cacheLoaded = true;
  const database = await db();
  if (cachedToken) {
    await database.put(STORE, cachedToken, TOKEN_KEY);
    await database.put(STORE, { savedAt: Date.now(), ...meta } satisfies TokenMeta, META_KEY);
  } else {
    await clearToken();
  }
}

export async function clearToken(): Promise<void> {
  cachedToken = null;
  cacheLoaded = true;
  try {
    const database = await db();
    await database.delete(STORE, TOKEN_KEY);
    await database.delete(STORE, META_KEY);
  } catch {
    // Nothing to do — the in-memory copy is already gone.
  }
}

export async function getTokenMeta(): Promise<TokenMeta | null> {
  try {
    const value = await (await db()).get(STORE, META_KEY);
    return (value as TokenMeta | undefined) ?? null;
  } catch {
    return null;
  }
}

export async function updateTokenMeta(patch: Partial<TokenMeta>): Promise<void> {
  try {
    const database = await db();
    const current = ((await database.get(STORE, META_KEY)) as TokenMeta | undefined) ?? {
      savedAt: Date.now(),
    };
    await database.put(STORE, { ...current, ...patch }, META_KEY);
  } catch {
    // Metadata is a convenience; losing it is not worth surfacing.
  }
}

/**
 * Redact a token for display. Never render the token itself: the Settings
 * screen shows this instead, so a shoulder-surfer or a screenshot does not
 * capture a credential that grants full account access.
 */
export function redactToken(token: string | null): string {
  if (!token) return 'none';
  if (token.length <= 8) return `${'•'.repeat(token.length)} (${token.length} chars)`;
  return `${'•'.repeat(token.length - 4)}${token.slice(-4)} (${token.length} chars)`;
}

/** Test hook: drop the in-memory cache without touching storage. */
export function __resetTokenCacheForTests(): void {
  cachedToken = null;
  cacheLoaded = false;
  dbPromise = null;
}
